<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-200 relative">
    <!-- Watermark -->
    <div class="fixed inset-0 flex items-center justify-center pointer-events-none z-0 opacity-[0.03] dark:opacity-[0.02]">
      <img src="/assets/images/atqan-logo.svg" alt="Atqan Watermark" class="w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 transform rotate-[-30deg]" />
    </div>

    <div class="relative z-10">
      <AppHeader />

      <div class="flex mt-14 sm:mt-16 min-h-[calc(100vh-3.5rem)] sm:min-h-[calc(100vh-4rem)]">

        <!-- Apps Sidebar -->
        <aside
          :class="[
            'fixed top-14 sm:top-16 h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)] z-40 flex flex-col',
            isRTL ? 'right-0 border-l' : 'left-0 border-r',
            'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700',
            'transition-all duration-300 ease-in-out',
            sidebarOpen ? 'w-72' : 'w-14',
          ]"
        >
          <!-- Sidebar Header -->
          <div class="flex items-center gap-2 px-3 py-3 border-b border-gray-200 dark:border-gray-700 min-h-[56px]">
            <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-violet-500/20">
              <UIcon name="i-heroicons-squares-2x2" class="w-4 h-4 text-white" />
            </div>
            <div v-if="sidebarOpen" class="flex-1 flex items-center justify-between min-w-0">
              <span class="text-sm font-semibold text-gray-900 dark:text-white">{{ t('nav.applications') }}</span>
              <UButton
                icon="i-heroicons-plus"
                size="xs"
                @click="$router.push('/apps/new')"
                :title="'New App'"
                class="flex-shrink-0"
              />
            </div>
          </div>

          <!-- Search -->
          <div v-if="sidebarOpen" class="px-2 py-2 border-b border-gray-200 dark:border-gray-700">
            <UInput
              v-model="searchQuery"
              :placeholder="t('sidebar.searchApps')"
              icon="i-heroicons-magnifying-glass"
              size="sm"
              :ui="{ base: 'text-xs' }"
            />
          </div>

          <!-- Apps List -->
          <nav class="flex-1 overflow-y-auto py-2">
            <!-- Loading skeleton -->
            <div v-if="loading" class="px-2 space-y-1">
              <div v-for="i in 5" :key="i" class="h-9 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
            </div>

            <!-- Empty state -->
            <div v-else-if="!loading && filteredApps.length === 0 && sidebarOpen" class="px-4 py-8 text-center">
              <UIcon name="i-heroicons-folder-open" class="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p class="text-xs text-gray-400 dark:text-gray-500">
                {{ searchQuery ? t('sidebar.noAppsMatch') : t('sidebar.noApps') }}
              </p>
            </div>

            <!-- Groups -->
            <template v-for="group in groupedApps" :key="group.status">
              <!-- Group Header -->
              <button
                v-if="sidebarOpen && group.apps.length > 0"
                @click="toggleGroup(group.status)"
                class="w-full flex items-center justify-between px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div class="flex items-center gap-2">
                  <span :class="['w-2 h-2 rounded-full flex-shrink-0', group.dotClass]" />
                  <span class="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    {{ group.label }}
                  </span>
                  <span class="text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded px-1 font-medium">
                    {{ group.apps.length }}
                  </span>
                </div>
                <UIcon
                  :name="collapsedGroups.has(group.status) ? 'i-heroicons-chevron-right' : 'i-heroicons-chevron-down'"
                  class="w-3 h-3 text-gray-400"
                />
              </button>

              <!-- Collapsed separator (icon-only mode) -->
              <div v-else-if="!sidebarOpen && group.apps.length > 0" class="flex justify-center py-1">
                <span :class="['w-1.5 h-1.5 rounded-full', group.dotClass]" :title="group.label" />
              </div>

              <!-- App Items -->
              <div v-if="!collapsedGroups.has(group.status)">
                <NuxtLink
                  v-for="app in group.apps"
                  :key="app.id"
                  :to="`/apps/${app.id}`"
                  class="group flex items-center gap-2.5 mx-2 px-2.5 py-2 rounded-lg transition-all duration-150 relative"
                  :class="[
                    isActiveApp(app.id)
                      ? 'bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                  ]"
                  :title="!sidebarOpen ? app.name : undefined"
                >
                  <!-- Active bar -->
                  <span
                    v-if="isActiveApp(app.id)"
                    :class="[
                      'absolute top-1/2 -translate-y-1/2 w-0.5 h-5 bg-violet-600 dark:bg-violet-400',
                      isRTL ? 'right-0 rounded-l' : 'left-0 rounded-r',
                    ]"
                  />

                  <!-- Status dot (always visible) -->
                  <span :class="['w-2 h-2 rounded-full flex-shrink-0', group.dotClass, app.deploy_status === 'deploying' ? 'animate-pulse' : '']" />

                  <!-- App info (expanded only) -->
                  <div v-if="sidebarOpen" class="min-w-0 flex-1">
                    <p class="text-xs font-medium truncate leading-tight">{{ app.name }}</p>
                    <p class="text-[10px] text-gray-400 dark:text-gray-500 truncate">{{ app.github_repo }}</p>
                  </div>

                  <!-- Quick actions (expanded + hover) -->
                  <div v-if="sidebarOpen" class="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <UDropdown :items="getAppActions(app)" @click.prevent>
                      <UButton
                        variant="ghost"
                        icon="i-heroicons-ellipsis-horizontal"
                        size="2xs"
                        color="gray"
                        @click.prevent.stop
                      />
                    </UDropdown>
                  </div>
                </NuxtLink>
              </div>
            </template>
          </nav>

          <!-- Bottom -->
          <div class="border-t border-gray-200 dark:border-gray-700 p-2 space-y-1">
            <button
              v-if="!sidebarOpen"
              @click="$router.push('/apps/new')"
              class="w-full flex items-center justify-center p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="New App"
            >
              <UIcon name="i-heroicons-plus" class="w-4 h-4" />
            </button>

            <button
              @click="refreshApps"
              class="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-150"
              :title="sidebarOpen ? undefined : 'Refresh'"
            >
              <UIcon :name="loading ? 'i-heroicons-arrow-path' : 'i-heroicons-arrow-path'" :class="['w-4 h-4 flex-shrink-0', loading ? 'animate-spin' : '']" />
              <span v-if="sidebarOpen" class="text-xs">{{ t('sidebar.refresh') }}</span>
            </button>

            <button
              @click="sidebarOpen = !sidebarOpen"
              class="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-150"
              :title="sidebarOpen ? t('sidebar.collapse') : t('sidebar.expand')"
            >
              <UIcon
                :name="isRTL ? (sidebarOpen ? 'i-heroicons-chevron-double-right' : 'i-heroicons-chevron-double-left') : (sidebarOpen ? 'i-heroicons-chevron-double-left' : 'i-heroicons-chevron-double-right')"
                class="w-4 h-4 flex-shrink-0"
              />
              <span v-if="sidebarOpen" class="text-xs">{{ t('sidebar.collapse') }}</span>
            </button>
          </div>
        </aside>

        <!-- Main Content -->
        <main
          :class="[
            'flex-1 transition-all duration-300 ease-in-out',
            isRTL
              ? (sidebarOpen ? 'mr-72' : 'mr-14')
              : (sidebarOpen ? 'ml-72' : 'ml-14'),
            'px-4 sm:px-6 lg:px-8 py-6',
          ]"
        >
          <slot />
        </main>
      </div>

      <UNotifications />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, provide } from 'vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { initializeLanguage, isRTL } = useLanguage();

const sidebarOpen = ref(true);
const searchQuery = ref('');
const collapsedGroups = ref<Set<string>>(new Set());
const loading = ref(false);
const apps = ref<any[]>([]);

const fetchApps = async () => {
  loading.value = true;
  try {
    const { authenticatedFetch } = useAuthenticatedFetch();
    const data = await authenticatedFetch('/api/v1/apps/');
    apps.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('Failed to fetch apps:', err);
  } finally {
    loading.value = false;
  }
};

const refreshApps = () => fetchApps();

provide('apps', apps);
provide('refreshApps', fetchApps);

const filteredApps = computed(() => {
  if (!searchQuery.value.trim()) return apps.value;
  const q = searchQuery.value.toLowerCase();
  return apps.value.filter(
    (a) =>
      a.name?.toLowerCase().includes(q) ||
      a.github_repo?.toLowerCase().includes(q) ||
      a.domain?.toLowerCase().includes(q)
  );
});

const STATUS_GROUPS = computed(() => [
  { status: 'running',      label: t('sidebar.status.running'),     dotClass: 'bg-green-500' },
  { status: 'deploying',    label: t('sidebar.status.deploying'),   dotClass: 'bg-yellow-500' },
  { status: 'error',        label: t('sidebar.status.failed'),      dotClass: 'bg-red-500' },
  { status: 'stopped',      label: t('sidebar.status.stopped'),     dotClass: 'bg-gray-400' },
  { status: 'not_deployed', label: t('sidebar.status.notDeployed'), dotClass: 'bg-gray-300 dark:bg-gray-600' },
]);

const groupedApps = computed(() =>
  STATUS_GROUPS.value.map((g) => ({
    ...g,
    apps: filteredApps.value.filter((a) => (a.deploy_status || 'not_deployed') === g.status),
  })).filter((g) => g.apps.length > 0)
);

const toggleGroup = (status: string) => {
  if (collapsedGroups.value.has(status)) {
    collapsedGroups.value.delete(status);
  } else {
    collapsedGroups.value.add(status);
  }
};

const fadeDir = computed(() => isRTL.value ? '6px' : '-6px');

const isActiveApp = (appId: number) => {
  return route.params.id === String(appId) || route.path.startsWith(`/apps/${appId}`);
};

const getAppActions = (app: any) => [
  [
    {
      label: 'View',
      icon: 'i-heroicons-eye',
      click: () => router.push(`/apps/${app.id}`),
    },
    {
      label: 'Edit',
      icon: 'i-heroicons-pencil-square',
      click: () => router.push(`/apps/${app.id}/edit`),
    },
    {
      label: 'Pull Requests',
      icon: 'i-heroicons-arrow-path-rounded-square',
      click: () => router.push(`/apps/${app.id}/pull-requests`),
    },
    {
      label: 'Deployments',
      icon: 'i-heroicons-rocket-launch',
      click: () => router.push(`/apps/${app.id}/deployments`),
    },
  ],
];

onMounted(async () => {
  initializeLanguage();
  await fetchApps();
  if (window.innerWidth < 768) {
    sidebarOpen.value = false;
  }
});
</script>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(v-bind(fadeDir));
}
</style>
